import ActiveIndicator from "@app/components/active-indicator.tsx";
import DeleteButton from "@app/components/delete-button.tsx";
import { EntryModel } from "@app/data/engine.ts";
import { formatUsd } from "@app/helpers/money-helper";
import { commaSeperate } from "@app/helpers/number-helper";
import { formatDate } from "@app/helpers/time-helper.ts";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Checkbox, Tooltip } from "@mui/material";
import { createColumnHelper, IconButton } from "bi-lib";

interface Props {
    catId: number;
    onEdit: (item: EntryModel) => void;
    onDelete: (id: string) => void;
    postingMode: boolean;
    selectedIds: string[];
    onSelectionChanged: (id: string) => void;
    allSelected: boolean;
    onToggleSelectAll: () => void;
    isAdmin: boolean;
    onViewReceipt: (id: string) => void;
    multiStaff: boolean;
    rateType: "billing" | "cost" | "internal";
    quantityTotal: number;
    billingAmountTotal: number;
    costAmountTotal: number;
    internalAmountTotal: number;
    rowCount: number;
}

const columnHelper = createColumnHelper<EntryModel>();
export const EntryTableTableDefinition = ({
    catId,
    onEdit,
    onDelete,
    postingMode,
    selectedIds,
    onSelectionChanged,
    allSelected,
    onToggleSelectAll,
    isAdmin,
    onViewReceipt,
    rateType,
    quantityTotal,
    billingAmountTotal,
    costAmountTotal,
    internalAmountTotal,
    rowCount,
}: Props) => {
    return [
        postingMode
            ? columnHelper.display({
                  id: "selections",
                  header: () => <Checkbox onChange={onToggleSelectAll} checked={allSelected} />,
                  cell: (item) => (
                      <Checkbox
                          onChange={() => onSelectionChanged(item.row.original.entryId!)}
                          checked={selectedIds.includes(item.row.original.entryId!)}
                      />
                  ),
              })
            : columnHelper.display({
                  id: "actions",
                  header: "Actions",
                  cell: (item) => (
                      <Box display={"flex"}>
                          <IconButton tooltip={"Edit"} onClick={() => onEdit(item.row.original)}>
                              <EditIcon />
                          </IconButton>
                          {(!item.row.original.isPosted || isAdmin) && (
                              <DeleteButton onDelete={() => onDelete(item.row.original.entryId!)} />
                          )}
                      </Box>
                  ),
                  footer: () => `${rowCount} Rows`,
              }),
        columnHelper.accessor((x) => x.itemDate, {
            id: "itemDate",
            header: "Date",
            cell: (item) => formatDate(item.getValue()),
        }),
        ...(catId != 1
            ? [columnHelper.accessor((x) => x.subcategory, { id: "subcategory", header: "Expense Type" })]
            : []),
        columnHelper.accessor((x) => x.staffName, { id: "staffName", header: "Staff" }),
        columnHelper.accessor((x) => x.assignNum, {
            id: "assignNum",
            header: "Assignment",
            cell: (item) => `${item.getValue()} ${item.row.original.assignDescription}`,
        }),
        ...(catId == 1
            ? [
                  columnHelper.accessor((x) => x.phaseCode, { id: "phaseCode", header: "Phase" }),
                  columnHelper.accessor((x) => x.workPackageCode, { id: "workPackageCode", header: "WP Code" }),
              ]
            : []),
        columnHelper.display({
            id: "workLocation",
            header: "Work Location",
            cell: (item) => `${item.row.original.workLocationState} - ${item.row.original.workLocationCity}`,
        }),
        ...(catId == 1
            ? [
                  columnHelper.accessor((x) => x.quantity, {
                      id: "quantity",
                      header: "Quantity",
                      cell: (item) => commaSeperate(item.getValue()),
                      footer: () => commaSeperate(quantityTotal),
                      alignment: "right",
                  }),
                  ...(rateType == "billing"
                      ? [
                            columnHelper.accessor((x) => x.billingRate, {
                                id: "billingRate",
                                header: "Billing Rate",
                                cell: (item) => formatUsd(item.getValue()),
                                alignment: "right",
                            }),
                            columnHelper.accessor((x) => x.billingAmount, {
                                id: "billingAmount",
                                header: "Billing Amount",
                                cell: (item) => formatUsd(item.getValue()),
                                footer: () => formatUsd(billingAmountTotal),
                                alignment: "right",
                            }),
                        ]
                      : rateType == "cost"
                        ? [
                              columnHelper.accessor((x) => x.costRate, {
                                  id: "costRate",
                                  header: "Cost Rate",
                                  cell: (item) => formatUsd(item.getValue()),
                                  alignment: "right",
                              }),
                              columnHelper.accessor((x) => x.costAmount, {
                                  id: "costAmount",
                                  header: "Cost Amount",
                                  cell: (item) => formatUsd(item.getValue()),
                                  footer: () => formatUsd(costAmountTotal),
                                  alignment: "right",
                              }),
                          ]
                        : [
                              columnHelper.accessor((x) => x.internalRate, {
                                  id: "internalRate",
                                  header: "Internal Rate",
                                  cell: (item) => formatUsd(item.getValue()),
                                  alignment: "right",
                              }),
                              columnHelper.accessor((x) => x.internalAmount, {
                                  id: "internalAmount",
                                  header: "Internal Amount",
                                  cell: (item) => formatUsd(item.getValue()),
                                  footer: () => formatUsd(internalAmountTotal),
                                  alignment: "right",
                              }),
                          ]),
              ]
            : [
                  rateType == "billing"
                      ? columnHelper.accessor((x) => x.billingAmount, {
                            id: "billingAmount",
                            header: "Billing Amount",
                            cell: (item) => formatUsd(item.getValue()),
                            footer: () => formatUsd(billingAmountTotal),
                        })
                      : rateType == "cost"
                        ? columnHelper.accessor((x) => x.costAmount, {
                              id: "costAmount",
                              header: "Cost Amount",
                              cell: (item) => formatUsd(item.getValue()),
                              footer: () => formatUsd(costAmountTotal),
                          })
                        : columnHelper.accessor((x) => x.internalAmount, {
                              id: "internalAmount",
                              header: "Internal Amount",
                              cell: (item) => formatUsd(item.getValue()),
                              footer: () => formatUsd(internalAmountTotal),
                          }),
              ]),
        columnHelper.accessor((x) => x.description, { id: "description", header: "Description" }),
        ...(catId == 1
            ? []
            : [
                  columnHelper.accessor((x) => x.isReceiptUploaded, {
                      id: "isReceiptUploaded",
                      header: "Receipt",
                      cell: (item) => {
                          const value = item.getValue();

                          if (value == true) {
                              return (
                                  <IconButton
                                      tooltip={"View Receipt"}
                                      onClick={() => onViewReceipt(item.row.original.entryId!)}
                                  >
                                      <ActiveIndicator isActive={value} />
                                  </IconButton>
                              );
                          }

                          if (!item.row.original.isReceiptRequired) {
                              return (
                                  <Box sx={{ px: 1 }}>
                                      <Tooltip title={"Receipt Not Required"} arrow>
                                          <DoNotDisturbOnIcon color={"disabled"} />
                                      </Tooltip>
                                  </Box>
                              );
                          }

                          return (
                              <Box sx={{ px: 1 }}>
                                  <Tooltip title={"Receipt Needed"} arrow>
                                      <Box>
                                          <ActiveIndicator isActive={value} />
                                      </Box>
                                  </Tooltip>
                              </Box>
                          );
                      },
                  }),
              ]),
        columnHelper.accessor((x) => x.isBillable, {
            id: "isBillable",
            header: "Billable",
            cell: (item) => <ActiveIndicator isActive={item.getValue()} />,
        }),
        columnHelper.accessor((x) => x.isInvoiceVisible, {
            id: "isInvoiceVisible",
            header: "Invoice Visible",
            cell: (item) => <ActiveIndicator isActive={item.getValue()} />,
        }),
        columnHelper.accessor((x) => x.isReimbursable, {
            id: "isReimbursable",
            header: "Reimbursable",
            cell: (item) => <ActiveIndicator isActive={item.getValue()} />,
        }),
        columnHelper.accessor((x) => x.isPosted, {
            id: "isPosted",
            header: "Posted",
            cell: (item) => <ActiveIndicator isActive={item.getValue()} />,
        }),
    ];
};
